import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, FileSpreadsheet, FileText, Percent, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { brl, pct } from "@/lib/format";
import { useLayout } from "@/context/LayoutContext";
import { useUnit } from "@/context/UnitContext";
import { exportMonthPdf, exportYearPdf, monthLabel, share, shiftMonth } from "@/lib/dre";
import { Button, Card, ErrorState, Loading, Page, PageHeader, Segmented } from "@/components/kit";
import { DreTable } from "@/components/dre/DreTable";
import { DreYear } from "@/components/dre/DreYear";
import { ExpensesCard } from "@/components/dre/ExpensesCard";
import { DreSettingsDialog } from "@/components/dre/DreSettingsDialog";
import { BreakEvenCard, GoalBadge } from "@/components/dre/BreakEvenCard";

const VIEWS = [{ value: "month", label: "Mensal" }, { value: "year", label: "Anual" }];
const today = () => new Date().toISOString().slice(0, 7);

function Kpis({ v, b }) {
  const items = [["Receita bruta", v.receita_bruta, null, "receita"], ["CMV", v.cmv, share(v.cmv, v.receita_bruta), "cmv"], ["EBITDA", v.ebitda, share(v.ebitda, v.receita_bruta), "ebitda"], ["Lucro líquido", v.lucro_liquido, share(v.lucro_liquido, v.receita_bruta), "lucro"]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {items.map(([l, val, p, id]) => (
        <Card key={id} className="flex flex-col gap-1" data-testid={`dre-kpi-${id}`}>
          <span className="caption">{l}</span>
          <span className="text-xl font-bold tnum" style={{ color: val < 0 ? "var(--danger)" : undefined }}>{brl(val)}</span>
          {p !== null && <span className="caption tnum">{pct(p)} da receita</span>}
          {id === "lucro" && <GoalBadge b={b} />}
        </Card>
      ))}
    </div>
  );
}

function Toolbar({ view, setView, month, setMonth, year, setYear, onPdf, onXlsx, onSettings }) {
  const label = view === "month" ? monthLabel(month) : year;
  const step = (d) => (view === "month" ? setMonth(shiftMonth(month, d)) : setYear(year + d));
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Segmented options={VIEWS} value={view} onChange={setView} testid="dre-view" />
      <div className="flex items-center gap-1 mr-auto">
        <button className="icon-btn" onClick={() => step(-1)} data-testid="dre-prev"><ChevronLeft size={18} /></button>
        <span className="label min-w-[140px] text-center" data-testid="dre-period-label">{label}</span>
        <button className="icon-btn" onClick={() => step(1)} data-testid="dre-next"><ChevronRight size={18} /></button>
      </div>
      <Button variant="ghost" size="sm" icon={Percent} onClick={onSettings} data-testid="dre-settings-button">Impostos e metas</Button>
      <Button variant="secondary" size="sm" icon={FileText} onClick={onPdf} data-testid="dre-export-pdf">PDF</Button>
      <Button variant="secondary" size="sm" icon={FileSpreadsheet} onClick={onXlsx} data-testid="dre-export-xlsx">Excel</Button>
    </div>
  );
}

export default function Dre() {
  const { layout } = useLayout();
  const { unit } = useUnit();
  const [view, setView] = useState("month");
  const [month, setMonth] = useState(today);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [settings, setSettings] = useState(false);
  const m = useQuery({ queryKey: ["dre", "month", month], queryFn: () => api(`/dre?month=${month}`), enabled: view === "month" || settings });
  const y = useQuery({ queryKey: ["dre", "year", year], queryFn: () => api(`/dre/year?year=${year}`), enabled: view === "year" });
  const q = view === "month" ? m : y;
  const title = [layout?.app_name, unit?.name].filter(Boolean).join(" · ");
  const pdf = () => { if (!q.data) return; const ok = view === "month" ? exportMonthPdf(q.data, title) : exportYearPdf(q.data, year, title); if (!ok) toast.error("Não foi possível abrir o PDF"); };
  const xlsx = async () => {
    const y0 = view === "month" ? Number(month.slice(0, 4)) : year;
    const blob = await api(`/dre/export.xlsx?year=${y0}`, { responseType: "blob" }).catch((e) => toast.error(e.message));
    if (!blob) return;
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `dre-${y0}.xlsx` });
    a.click(); URL.revokeObjectURL(a.href);
  };
  const cur = m.data?.current;
  return (
    <Page>
      <PageHeader title="DRE" subtitle="Demonstração do resultado · faturamento e CMV vêm das conferências" back="/menu" />
      <div className="flex flex-col gap-4">
        <Toolbar view={view} setView={setView} month={month} setMonth={setMonth} year={year} setYear={setYear} onPdf={pdf} onXlsx={xlsx} onSettings={() => setSettings(true)} />
        {q.isLoading ? <Loading /> : q.isError ? <ErrorState message={q.error.message} onRetry={q.refetch} /> : view === "year" ? <DreYear d={y.data} /> : (
          <>
            {!cur.conferences && <Card className="flex gap-2 text-sm" data-testid="dre-no-conferences"><Info size={16} className="shrink-0 mt-0.5" /> Nenhuma conferência neste mês · o faturamento e o CMV aparecem quando houver conferências no período.</Card>}
            {cur.open_conferences > 0 && <Card className="flex gap-2 text-sm" data-testid="dre-open-conferences"><Info size={16} className="shrink-0 mt-0.5" /> {cur.open_conferences} conferência(s) ainda aberta(s) neste mês · os números podem mudar até serem finalizadas.</Card>}
            <Kpis v={cur.values} b={cur.breakeven} />
            <BreakEvenCard b={cur.breakeven} revenue={cur.values.receita_bruta} />
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-4 items-start [&>*]:min-w-0">
              <DreTable d={m.data} />
              <ExpensesCard month={month} expenses={cur.expenses} groups={m.data.groups} />
            </div>
          </>
        )}
      </div>
      {settings && m.data && <DreSettingsDialog open onOpenChange={setSettings} settings={m.data.settings} />}
    </Page>
  );
}
